import type { ChamadaHistoricoDto } from '@sheepcount/shared';
import { History } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { formatDataHoraBR } from '@/lib/format';

interface Mudanca {
  nome: string;
  presente: boolean;
}

/** O que mudou de `anterior` para `atual` (alunos que trocaram de estado ou entraram na chamada). */
function mudancas(atual: ChamadaHistoricoDto, anterior: ChamadaHistoricoDto): Mudanca[] {
  const antes = new Map(anterior.presencas.map((p) => [p.alunoId, p.presente]));
  return atual.presencas
    .filter((p) => antes.get(p.alunoId) !== p.presente)
    .map((p) => ({ nome: p.nome, presente: p.presente }));
}

interface Props {
  aulaId: string;
  /** Versão atual da chamada — o registro dela não oferece "restaurar". */
  versaoAtual: number;
  restaurando: boolean;
  onRestaurar: (registro: ChamadaHistoricoDto) => Promise<void>;
}

/**
 * Histórico de salvamentos de uma chamada (quem, quando, o que mudou), com
 * opção de restaurar uma versão anterior — desfaz acidentes como relançar a
 * chamada partindo de "todos FALTA".
 */
export function ChamadaHistorico({ aulaId, versaoAtual, restaurando, onRestaurar }: Props) {
  const [registros, setRegistros] = useState<ChamadaHistoricoDto[] | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [confirmando, setConfirmando] = useState<string | null>(null);

  async function carregar() {
    setCarregando(true);
    setErro(null);
    try {
      setRegistros(await api<ChamadaHistoricoDto[]>(`/aulas/${aulaId}/historico`));
    } catch (err) {
      setErro(err instanceof Error ? err.message : 'Erro ao carregar o histórico');
    } finally {
      setCarregando(false);
    }
  }

  if (registros === null) {
    return (
      <div className="mt-4">
        <Button variant="ghost" size="sm" disabled={carregando} onClick={() => void carregar()}>
          <History size={14} />
          {carregando ? 'Carregando…' : 'Ver histórico de alterações'}
        </Button>
        {erro && <div className="mt-1 text-[12px] text-danger">{erro}</div>}
      </div>
    );
  }

  return (
    <div className="mt-5">
      <div className="mb-2 text-[13px] font-semibold text-muted">Histórico de alterações</div>
      {registros.length === 0 ? (
        <div className="text-[13px] text-muted">Nenhum registro.</div>
      ) : (
        <div className="flex flex-col gap-2">
          {registros.map((registro, i) => {
            const anterior = registros[i + 1];
            const diff = anterior ? mudancas(registro, anterior) : [];
            const obsMudou = anterior !== undefined && anterior.observacoes !== registro.observacoes;
            const presentes = registro.presencas.filter((p) => p.presente).length;
            const atual = registro.versao === versaoAtual;
            return (
              <div key={registro.id} className="rounded-lg border border-border px-3.5 py-3">
                <div className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-0.5">
                  <div className="text-[13px] font-semibold">
                    {registro.versao === 0 && registro.catequistaNome === null
                      ? 'Registro inicial'
                      : (registro.catequistaNome ?? 'Professor removido')}
                    {atual && <span className="ml-1.5 font-normal text-success">· atual</span>}
                  </div>
                  <div className="text-[11.5px] text-muted">
                    {formatDataHoraBR(registro.criadoEm)} · {presentes}/{registro.presencas.length}{' '}
                    presentes
                  </div>
                </div>
                {!anterior && (
                  <div className="mt-1.5 text-[12.5px] text-muted">Primeiro lançamento</div>
                )}
                {anterior && (
                  <ul className="mt-1.5 text-[12.5px]">
                    {diff.map((m) => (
                      <li key={m.nome} className={m.presente ? 'text-success' : 'text-danger'}>
                        {m.presente ? '+ ' : '− '}
                        {m.nome} → {m.presente ? 'presente' : 'falta'}
                      </li>
                    ))}
                    {obsMudou && <li className="text-fg-soft">Observação alterada</li>}
                    {diff.length === 0 && !obsMudou && (
                      <li className="text-muted">Salvo sem mudanças</li>
                    )}
                  </ul>
                )}
                {!atual &&
                  (confirmando === registro.id ? (
                    <div className="mt-2 flex flex-wrap items-center justify-end gap-2">
                      <span className="text-[12px] text-fg-soft">
                        Voltar a chamada para esta versão?
                      </span>
                      <Button variant="secondary" size="sm" onClick={() => setConfirmando(null)}>
                        Cancelar
                      </Button>
                      <Button
                        variant="destructive-outline"
                        size="sm"
                        disabled={restaurando}
                        onClick={async () => {
                          await onRestaurar(registro);
                          setConfirmando(null);
                          await carregar();
                        }}
                      >
                        Restaurar
                      </Button>
                    </div>
                  ) : (
                    <div className="mt-1.5 flex justify-end">
                      <Button variant="ghost" size="sm" onClick={() => setConfirmando(registro.id)}>
                        Restaurar esta versão
                      </Button>
                    </div>
                  ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
